import React, { useState, useEffect } from 'react';
import { apiClient } from '../../../../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BG_SURFACE,
  BORDER_LIGHT,
  FONT_HEADING,
  BG_CARD,
} from '../designTokens';
import { RunConfigurationModal } from './RunConfigurationModal';
import { BatchResultsView } from './BatchResultsView';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Stats {
  unseatedCount: number;
  pendingRunExists: boolean;
}

interface Rule {
  id: string;
  name: string;
  ruleType: string;
  priority: number;
  enabled: boolean;
}

interface ComparisonResult {
  name: string;
  totalAssigned: number;
  totalUnplaced: number;
  splits: number;
  tablesUsed: number;
  score: number;
}

interface CompareConfig {
  name: string;
  ruleIds: string[];
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  container: {},
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  title: {
    fontSize: '0.95rem',
    fontFamily: FONT_HEADING,
    fontWeight: 600,
    color: TEXT_DARK,
    margin: 0,
  },
  startBtn: {
    padding: '0.5rem 1.1rem',
    fontSize: '0.82rem',
    fontWeight: 600,
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  seatEveryoneBtn: {
    padding: '0.5rem 1.1rem',
    fontSize: '0.82rem',
    fontWeight: 600,
    backgroundColor: '#2e7d32',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: '0.5rem',
  },
  seatEveryoneBtnDisabled: {
    padding: '0.5rem 1.1rem',
    fontSize: '0.82rem',
    fontWeight: 600,
    backgroundColor: '#a5d6a7',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'not-allowed',
    marginRight: '0.5rem',
  },
  empty: {
    textAlign: 'center' as const,
    padding: '3rem 1rem',
    color: TEXT_MUTED,
    fontSize: '0.85rem',
    backgroundColor: BG_SURFACE,
    borderRadius: '6px',
    border: `1px dashed ${BORDER_LIGHT}`,
  },
  resetBtn: {
    marginTop: '1rem',
    padding: '0.4rem 0.8rem',
    fontSize: '0.8rem',
    fontWeight: 500,
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  // Compare section
  compareSection: {
    marginTop: '1.5rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    overflow: 'hidden',
  },
  compareSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.6rem 0.85rem',
    backgroundColor: BG_SURFACE,
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  compareSectionBody: {
    padding: '0.85rem',
    borderTop: `1px solid ${BORDER_LIGHT}`,
  },
  configSlot: {
    marginBottom: '0.75rem',
    padding: '0.6rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    backgroundColor: BG_CARD,
  },
  configInput: {
    padding: '0.35rem 0.5rem',
    fontSize: '0.8rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    outline: 'none',
    width: '140px',
    marginBottom: '0.4rem',
  },
  ruleCheckboxList: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.3rem 0.75rem',
    fontSize: '0.78rem',
  },
  compareBtn: {
    padding: '0.45rem 1rem',
    fontSize: '0.82rem',
    fontWeight: 600,
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  compareBtnDisabled: {
    padding: '0.45rem 1rem',
    fontSize: '0.82rem',
    fontWeight: 600,
    backgroundColor: '#a5d6a7',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'not-allowed',
    marginTop: '0.5rem',
  },
  resultsTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.8rem',
    marginTop: '0.75rem',
  },
  th: {
    padding: '0.45rem 0.5rem',
    textAlign: 'left' as const,
    borderBottom: `2px solid ${BORDER_LIGHT}`,
    fontWeight: 600,
    color: TEXT_DARK,
    fontSize: '0.75rem',
    textTransform: 'uppercase' as const,
  },
  td: {
    padding: '0.45rem 0.5rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    color: TEXT_DARK,
  },
  useConfigBtn: {
    padding: '0.25rem 0.5rem',
    fontSize: '0.72rem',
    fontWeight: 500,
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
  },
  error: {
    padding: '0.5rem 0.75rem',
    color: '#d32f2f',
    fontSize: '0.8rem',
    backgroundColor: '#fdecea',
    borderRadius: '4px',
    marginBottom: '0.75rem',
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

export function RunSection() {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentRun, setCurrentRun] = useState<any>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [seatEveryoneLoading, setSeatEveryoneLoading] = useState(false);
  const [error, setError] = useState('');

  // Compare state
  const [compareExpanded, setCompareExpanded] = useState(false);
  const [rules, setRules] = useState<Rule[]>([]);
  const [configs, setConfigs] = useState<CompareConfig[]>([
    { name: 'Config A', ruleIds: [] },
    { name: 'Config B', ruleIds: [] },
  ]);
  const [compareResults, setCompareResults] = useState<ComparisonResult[] | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [selectedRuleIdsForModal, setSelectedRuleIdsForModal] = useState<string[] | null>(null);

  // Fetch stats on mount
  useEffect(() => {
    apiClient<Stats>('/batch-assignment/stats')
      .then(setStats)
      .catch(() => {});
  }, [currentRun]);

  // Fetch rules for compare section
  useEffect(() => {
    if (compareExpanded && rules.length === 0) {
      apiClient<Rule[]>('/batch-assignment/rules')
        .then((data) => {
          setRules(data);
          // Pre-populate first config with all enabled rules
          setConfigs((prev) => [
            { ...prev[0], ruleIds: data.filter((r) => r.enabled).map((r) => r.id) },
            prev[1],
          ]);
        })
        .catch(() => {});
    }
  }, [compareExpanded, rules.length]);

  const handleRunCreated = (runData: any) => {
    setCurrentRun(runData);
    setModalOpen(false);
  };

  const handleApproved = () => {
    setCurrentRun(null);
  };

  const handleSeatEveryone = async () => {
    if (!stats || stats.unseatedCount === 0 || stats.pendingRunExists) return;

    const confirmed = window.confirm(
      `This will seat all ${stats.unseatedCount} unseated guests using all enabled rules. Continue?`,
    );
    if (!confirmed) return;

    setSeatEveryoneLoading(true);
    setError('');

    try {
      // Get all enabled rules
      const allRules = await apiClient<Rule[]>('/batch-assignment/rules');
      const enabledRuleIds = allRules.filter((r) => r.enabled).map((r) => r.id);

      if (enabledRuleIds.length === 0) {
        setError('No enabled rules found. Create and enable rules first.');
        return;
      }

      // Execute run with AllUnseated mode
      const runData = await apiClient('/batch-assignment/runs', {
        method: 'POST',
        body: {
          guestSelectionMode: 'AllUnseated',
          ruleIds: enabledRuleIds,
        },
      });
      setCurrentRun(runData);
    } catch (err: unknown) {
      const apiErr = err as { statusCode?: number; message?: string };
      if (apiErr?.statusCode === 409) {
        setError('A run is already pending. Approve or reject it before starting a new one.');
      } else {
        setError(apiErr?.message || 'Failed to execute seat everyone.');
      }
    } finally {
      setSeatEveryoneLoading(false);
    }
  };

  const handleToggleConfigRule = (configIndex: number, ruleId: string) => {
    setConfigs((prev) => {
      const updated = [...prev];
      const config = { ...updated[configIndex] };
      if (config.ruleIds.includes(ruleId)) {
        config.ruleIds = config.ruleIds.filter((id) => id !== ruleId);
      } else {
        config.ruleIds = [...config.ruleIds, ruleId];
      }
      updated[configIndex] = config;
      return updated;
    });
  };

  const handleConfigNameChange = (configIndex: number, name: string) => {
    setConfigs((prev) => {
      const updated = [...prev];
      updated[configIndex] = { ...updated[configIndex], name };
      return updated;
    });
  };

  const handleCompare = async () => {
    const validConfigs = configs.filter((c) => c.ruleIds.length > 0);
    if (validConfigs.length < 2) {
      setError('Select rules for at least 2 configurations to compare.');
      return;
    }

    setCompareLoading(true);
    setError('');
    setCompareResults(null);

    try {
      const data = await apiClient<{ results: ComparisonResult[] }>(
        '/batch-assignment/runs/compare',
        {
          method: 'POST',
          body: { configs: validConfigs },
        },
      );
      setCompareResults(data.results);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || 'Failed to compare configurations.');
    } finally {
      setCompareLoading(false);
    }
  };

  const handleUseConfig = (configIndex: number) => {
    const config = configs[configIndex];
    setSelectedRuleIdsForModal(config.ruleIds);
    setModalOpen(true);
  };

  const seatEveryoneDisabled =
    !stats ||
    stats.pendingRunExists ||
    stats.unseatedCount === 0 ||
    seatEveryoneLoading;

  const bestScore = compareResults
    ? Math.max(...compareResults.map((r) => r.score))
    : 0;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>New Assignment Run</h3>
        {!currentRun && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button
              style={seatEveryoneDisabled ? styles.seatEveryoneBtnDisabled : styles.seatEveryoneBtn}
              onClick={handleSeatEveryone}
              disabled={seatEveryoneDisabled}
              aria-label="Seat all unseated guests"
            >
              {seatEveryoneLoading
                ? 'Running...'
                : `Seat Everyone${stats ? ` (${stats.unseatedCount} guests)` : ''}`}
            </button>
            <button
              style={styles.startBtn}
              onClick={() => setModalOpen(true)}
              aria-label="Start new batch assignment run"
            >
              Start New Run
            </button>
          </div>
        )}
      </div>

      {error && <div style={styles.error} role="alert">{error}</div>}

      {!currentRun && (
        <>
          <div style={styles.empty}>
            <p>Click "Start New Run" to configure and execute a batch seat assignment.</p>
            <p style={{ fontSize: '0.78rem', marginTop: '0.5rem', color: TEXT_MUTED }}>
              The batch engine will propose seat assignments based on your rules.
              You can review and approve them before they are applied.
            </p>
          </div>

          {/* Compare Configurations Section */}
          <div style={styles.compareSection}>
            <div
              style={styles.compareSectionHeader}
              onClick={() => setCompareExpanded(!compareExpanded)}
              role="button"
              aria-expanded={compareExpanded}
              aria-label="Toggle compare configurations section"
            >
              <span>📊 Compare Configurations</span>
              <span>{compareExpanded ? '▾' : '▸'}</span>
            </div>

            {compareExpanded && (
              <div style={styles.compareSectionBody}>
                {configs.map((config, idx) => (
                  <div key={idx} style={styles.configSlot}>
                    <input
                      style={styles.configInput}
                      type="text"
                      value={config.name}
                      onChange={(e) => handleConfigNameChange(idx, e.target.value)}
                      aria-label={`Configuration ${idx + 1} name`}
                    />
                    <div style={styles.ruleCheckboxList}>
                      {rules.map((rule) => (
                        <label key={rule.id} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={config.ruleIds.includes(rule.id)}
                            onChange={() => handleToggleConfigRule(idx, rule.id)}
                          />
                          <span>{rule.name}</span>
                        </label>
                      ))}
                      {rules.length === 0 && (
                        <span style={{ color: TEXT_MUTED }}>Loading rules...</span>
                      )}
                    </div>
                  </div>
                ))}

                <button
                  style={compareLoading ? styles.compareBtnDisabled : styles.compareBtn}
                  onClick={handleCompare}
                  disabled={compareLoading}
                >
                  {compareLoading ? 'Comparing...' : 'Compare'}
                </button>

                {/* Results Table */}
                {compareResults && (
                  <table style={styles.resultsTable}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Config</th>
                        <th style={styles.th}>Assigned</th>
                        <th style={styles.th}>Unplaced</th>
                        <th style={styles.th}>Splits</th>
                        <th style={styles.th}>Tables</th>
                        <th style={styles.th}>Score</th>
                        <th style={styles.th}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {compareResults.map((result, idx) => (
                        <tr key={idx}>
                          <td style={styles.td}>{result.name}</td>
                          <td style={styles.td}>{result.totalAssigned}</td>
                          <td style={styles.td}>{result.totalUnplaced}</td>
                          <td style={styles.td}>{result.splits}</td>
                          <td style={styles.td}>{result.tablesUsed}</td>
                          <td
                            style={{
                              ...styles.td,
                              fontWeight: 600,
                              color: result.score === bestScore ? '#2e7d32' : TEXT_DARK,
                            }}
                          >
                            {result.score}%
                          </td>
                          <td style={styles.td}>
                            <button
                              style={styles.useConfigBtn}
                              onClick={() => handleUseConfig(idx)}
                              aria-label={`Use ${result.name} configuration`}
                            >
                              Use This Config
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {currentRun && (
        <>
          <BatchResultsView run={currentRun} onApproved={handleApproved} />
          <button style={styles.resetBtn} onClick={() => setCurrentRun(null)}>
            ← Start Another Run
          </button>
        </>
      )}

      <RunConfigurationModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedRuleIdsForModal(null); }}
        onRunCreated={handleRunCreated}
        preSelectedRuleIds={selectedRuleIdsForModal}
      />
    </div>
  );
}
